<template>
  <article class="pi-card" :class="`is-${item.paymentStatus || 'unknown'}`">
    <!-- ==================== Error dialog + toast (unchanged) ==================== -->
    <base-dialog :show="!!error" title="An error occurred!" @close="handleError">
      <p>{{ error }}</p>
    </base-dialog>
    <base-toast
      v-if="!!baseToast.message"
      :message="baseToast.message"
      :type="baseToast.type"
      @close="handleBaseToast"
    ></base-toast>

    <!-- ==================== Modals (teleported) ==================== -->
    <teleport to="body">
      <!-- Approve -->
      <transition name="modal-fade">
        <div v-if="showPayInApproveModal" class="pi-modal-backdrop" @click.self="showPayInApproveModal = false">
          <div class="pi-modal-card pi-modal-wide">
            <div class="pi-modal-head">
              <div>
                <div class="pi-modal-eyebrow">Pay-in</div>
                <div class="pi-modal-title">Approve order</div>
                <div class="pi-modal-sub">Enter the bank UTR to confirm this pay-in.</div>
              </div>
              <button type="button" class="pi-modal-close" @click="showPayInApproveModal = false">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                </svg>
              </button>
            </div>
            <div class="pi-modal-body">
              <pay-in-approve-form
                :key="item.receiptId"
                :item="selectedItem"
                @form-response="formResponse"
              ></pay-in-approve-form>
            </div>
          </div>
        </div>
      </transition>

      <!-- Add UTR -->
      <transition name="modal-fade">
        <div v-if="showAddUtrModal" class="pi-modal-backdrop" @click.self="showAddUtrModal = false">
          <div class="pi-modal-card pi-modal-wide">
            <div class="pi-modal-head">
              <div>
                <div class="pi-modal-eyebrow">Pay-in</div>
                <div class="pi-modal-title">Add UTR</div>
                <div class="pi-modal-sub">Enter a UTR or upload a payment screenshot.</div>
              </div>
              <button type="button" class="pi-modal-close" @click="showAddUtrModal = false">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                </svg>
              </button>
            </div>
            <div class="pi-modal-body">
              <add-utr-form
                :key="item.receiptId"
                :item="selectedItem"
                @form-response="utrFormResponse"
              ></add-utr-form>
            </div>
          </div>
        </div>
      </transition>

      <!-- Client edit -->
      <transition name="modal-fade">
        <div v-if="showClientEditModal" class="pi-modal-backdrop" @click.self="showClientEditModal = false">
          <div class="pi-modal-card">
            <div class="pi-modal-head">
              <div>
                <div class="pi-modal-eyebrow">Pay-in</div>
                <div class="pi-modal-title">Edit order</div>
                <div class="pi-modal-sub">Update the customer UTR or the amount on your order.</div>
              </div>
              <button type="button" class="pi-modal-close" @click="showClientEditModal = false">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                </svg>
              </button>
            </div>
            <div class="pi-modal-body">
              <client-edit-order-form
                :key="item.receiptId"
                :item="selectedItem"
                @close="showClientEditModal = false"
                @updated="handleClientUpdate"
              ></client-edit-order-form>
            </div>
          </div>
        </div>
      </transition>

      <!-- Change bank -->
      <transition name="modal-fade">
        <div v-if="showChangeBankModal" class="pi-modal-backdrop" @click.self="showChangeBankModal = false">
          <div class="pi-modal-card pi-modal-wide">
            <div class="pi-modal-head">
              <div>
                <div class="pi-modal-eyebrow">Override</div>
                <div class="pi-modal-title">Change bank</div>
                <div class="pi-modal-sub">Move this order to a different bank. Balance impact is reversed atomically.</div>
              </div>
              <button type="button" class="pi-modal-close" @click="showChangeBankModal = false">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                </svg>
              </button>
            </div>
            <div class="pi-modal-body">
              <change-bank-form
                :key="`changebank-${item.refID}`"
                :order-row="selectedItem"
                @close="showChangeBankModal = false"
                @changed="handleBankChanged"
              ></change-bank-form>
            </div>
          </div>
        </div>
      </transition>

      <!-- Update UTR -->
      <transition name="modal-fade">
        <div v-if="showUpdateUtrModal" class="pi-modal-backdrop" @click.self="showUpdateUtrModal = false">
          <div class="pi-modal-card">
            <div class="pi-modal-head pi-modal-head-warn">
              <div>
                <div class="pi-modal-eyebrow warn">Override</div>
                <div class="pi-modal-title">Update UTR</div>
                <div class="pi-modal-sub">Correct the UTR on this approved order. A fresh callback fires.</div>
              </div>
              <button type="button" class="pi-modal-close" @click="showUpdateUtrModal = false">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                </svg>
              </button>
            </div>
            <div class="pi-modal-body">
              <update-utr-form
                :key="`utr-${item.refID}`"
                :order-row="selectedItem"
                @close="showUpdateUtrModal = false"
                @updated="handleUtrUpdated"
              ></update-utr-form>
            </div>
          </div>
        </div>
      </transition>

      <!-- Mark failed -->
      <transition name="modal-fade">
        <div v-if="showMarkFailedModal" class="pi-modal-backdrop" @click.self="showMarkFailedModal = false">
          <div class="pi-modal-card">
            <div class="pi-modal-head pi-modal-head-danger">
              <div>
                <div class="pi-modal-eyebrow danger">Override · Destructive</div>
                <div class="pi-modal-title">Mark pay-in failed</div>
                <div class="pi-modal-sub">Flip this order to failed and fire the merchant's failed-order callback.</div>
              </div>
              <button type="button" class="pi-modal-close" @click="showMarkFailedModal = false">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                </svg>
              </button>
            </div>
            <div class="pi-modal-body">
              <mark-failed-form
                :key="`fail-${item.refID}`"
                :order-row="selectedItem"
                @close="showMarkFailedModal = false"
                @failed="handleMarkedFailed"
              ></mark-failed-form>
            </div>
          </div>
        </div>
      </transition>

      <!-- Client cancel confirm (09082026) -->
      <transition name="modal-fade">
        <div v-if="showClientCancelModal" class="pi-modal-backdrop" @click.self="showClientCancelModal = false">
          <div class="pi-modal-card">
            <div class="pi-modal-head pi-modal-head-danger">
              <div>
                <div class="pi-modal-eyebrow danger">Destructive</div>
                <div class="pi-modal-title">Cancel this pay-in?</div>
                <div class="pi-modal-sub">The order is marked failed, its UTR (if any) is removed, and your system receives the failed-order callback. This cannot be undone.</div>
              </div>
              <button type="button" class="pi-modal-close" @click="showClientCancelModal = false">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                </svg>
              </button>
            </div>
            <div class="pi-modal-body">
              <div style="display: flex; gap: 10px; justify-content: flex-end;">
                <button type="button" class="side-btn side-btn-ghost" :disabled="cancelBusy" @click="showClientCancelModal = false">Keep order</button>
                <button type="button" class="side-btn side-btn-primary" :disabled="cancelBusy" @click="confirmClientCancel">{{ cancelBusy ? 'Cancelling…' : 'Yes, cancel order' }}</button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </teleport>

    <!-- ==================== Left: info ==================== -->
    <div class="pi-info">
      <!-- Identity row -->
      <div class="pi-identity">
        <div class="pi-receipt">
          <span class="receipt-label">Receipt</span>
          <span class="receipt-value">{{ item.receiptId }}</span>
          <base-copy-button :text="item.receiptId" title="Copy Receipt ID" />
        </div>
        <div class="pi-badges">
          <span
            v-if="isReassignedApproval"
            class="pill pill-info"
            :title="`Originally assigned to ${item.original_validator_username}`"
          >Reassigned from {{ item.original_validator_username }}</span>
          <span
            v-if="isLateApproval"
            class="pill pill-warn"
            title="Order had expired before this approval landed via a bank statement upload"
          >Late approval</span>
        </div>
      </div>

      <!-- Info grid -->
      <div class="pi-grid">
        <!-- Left column -->
        <div class="pi-col">
          <div v-if="isVendor && item.clientName" class="pi-field">
            <div class="field-label">Client</div>
            <div class="field-value">
              <span>{{ item.clientName }}</span>
              <base-copy-button :text="item.clientName" title="Copy Client" />
            </div>
          </div>
          <div class="pi-field">
            <div class="field-label">Customer name</div>
            <div class="field-value">
              <span>{{ item.customerName }}</span>
              <base-copy-button :text="item.customerName" title="Copy Customer Name" />
            </div>
          </div>
          <div v-if="isAdmin" class="pi-field">
            <div class="field-label">Client UPI</div>
            <div class="field-value">
              <span class="mono">{{ item.customerUPIID }}</span>
              <base-copy-button :text="item.customerUPIID" title="Copy Client UPI" />
            </div>
          </div>
          <div class="pi-field">
            <div class="field-label">Created</div>
            <div class="field-value">{{ formatDate(item.createdAt) }}</div>
          </div>
          <div v-if="item.paymentStatus === 'approved'" class="pi-field">
            <div class="field-label">Approved at</div>
            <div class="field-value">{{ formatDate(item.updatedAt) }}</div>
          </div>
          <div v-if="(isAdmin || isSubAdmin) && getApprovedBy" class="pi-field">
            <div class="field-label">Approved by</div>
            <div class="field-value">
              <span>{{ getApprovedBy }}</span>
              <base-copy-button :text="getApprovedBy" title="Copy Approved By" />
            </div>
          </div>
          <div v-if="(isAdmin || isSubAdmin || isUser) && item.approval_method && item.paymentStatus === 'approved'" class="pi-field">
            <div class="field-label">Method</div>
            <div class="field-value">
              <span class="method-chip" :class="`method-${approvalMethodTone}`">{{ getApprovalMethod }}</span>
            </div>
          </div>
          <div v-if="item.created_by" class="pi-field">
            <div class="field-label">Created by</div>
            <div class="field-value">
              <span>{{ getCreatedBy }}</span>
              <base-copy-button :text="getCreatedBy" title="Copy Order Creator" />
            </div>
          </div>
        </div>

        <!-- Right column -->
        <div class="pi-col">
          <div class="pi-field">
            <div class="field-label">Order ID</div>
            <div class="field-value">
              <span class="mono break">{{ item.merchantOrderId }}</span>
              <base-copy-button :text="item.merchantOrderId" title="Copy Order ID" />
            </div>
          </div>
          <div class="pi-field">
            <div class="field-label">Payment URL</div>
            <div class="field-value">
              <a :href="getPaymentUrl" target="_blank" class="link mono">/pay/{{ item.refID }}</a>
              <base-copy-button :text="getPaymentUrl" title="Copy URL" />
            </div>
          </div>
          <div v-if="isAdmin || isSubAdmin" class="pi-field">
            <div class="field-label">Assigned to</div>
            <div class="field-value">
              <span>{{ item.validatorUsername }}</span>
              <base-copy-button :text="item.validatorUsername" title="Copy Assigned To" />
            </div>
          </div>
          <div v-if="isVendor && (item.branch_name || item.branch_username)" class="pi-field">
            <div class="field-label">Branch</div>
            <div class="field-value">{{ item.branch_name || item.branch_username }}</div>
          </div>
          <div v-if="isAdmin || isSubAdmin || isClient || isClientPartner" class="pi-field">
            <div class="field-label">Assigned UPI</div>
            <div class="field-value">
              <span class="mono break">{{ item.validatorUPIID }}</span>
              <base-copy-button :text="item.validatorUPIID" title="Copy Assigned UPI" />
            </div>
          </div>
          <div v-if="!isAdmin && item.customerUPIID" class="pi-field">
            <div class="field-label">Client UPI</div>
            <div class="field-value">
              <span class="mono">{{ item.customerUPIID }}</span>
              <base-copy-button :text="item.customerUPIID" title="Copy Client UPI" />
            </div>
          </div>
          <div v-if="item.transactionID" class="pi-field">
            <div class="field-label">UTR</div>
            <div class="field-value">
              <span class="mono">{{ item.transactionID }}</span>
              <base-copy-button :text="item.transactionID" title="Copy UTR" />
            </div>
          </div>
          <div v-if="item.customerUtr" class="pi-field">
            <div class="field-label">Customer UTR</div>
            <div class="field-value">
              <span class="mono">{{ item.customerUtr }}</span>
              <base-copy-button :text="item.customerUtr" title="Copy Customer UTR" />
            </div>
          </div>
          <div class="pi-field">
            <div class="field-label">Payment method</div>
            <div class="field-value">{{ item.mode || 'N/A' }}</div>
          </div>
          <div v-if="item.upload_screenshot" class="pi-field">
            <div class="field-label">Attachment</div>
            <div class="field-value">
              <a :href="item.upload_screenshot" target="_blank" class="link">View screenshot</a>
              <base-copy-button :text="item.upload_screenshot" title="Copy Attachment" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== Right: status + actions ==================== -->
    <div class="pi-side">
      <div class="status-pill" :class="`status-${statusTone}`">{{ item.paymentStatus }}</div>
      <div v-if="item.amount" class="amount-value">₹{{ formatMoney(getAmount) }}</div>

      <div class="side-actions">
        <button
          v-if="showApproveBtn"
          type="button"
          class="side-btn side-btn-primary"
          @click.stop="approveOrder"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
            <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          Approve
        </button>

        <button
          v-if="isSubAdmin && can('payin.edit_utr') && item.paymentStatus === 'pending' && !item.customerUtr"
          type="button"
          class="side-btn side-btn-ghost"
          @click.stop="addUtr"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 5v14M5 12h14" stroke-linecap="round" />
          </svg>
          Add UTR
        </button>

        <button
          v-if="canEditOrder"
          type="button"
          class="side-btn side-btn-secondary"
          @click.stop="editOrder"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 20h9" stroke-linecap="round" />
            <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          Edit
        </button>

        <button
          v-if="showClientCancelBtn"
          type="button"
          class="side-btn side-btn-ghost"
          @click.stop="showClientCancelModal = true"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="9" />
            <path d="M15 9l-6 6M9 9l6 6" stroke-linecap="round" />
          </svg>
          Cancel
        </button>

        <button
          v-if="showResendCallbackBtn"
          type="button"
          class="side-btn side-btn-ghost"
          :disabled="resendBusy"
          @click.stop="resendCallback"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M23 4v6h-6M1 20v-6h6" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          {{ resendBusy ? 'Sending…' : 'Resend callback' }}
        </button>

        <!-- Overrides menu (vendor-only power actions) -->
        <div v-if="showOverridesTrigger" class="overrides-wrap">
          <button
            type="button"
            class="side-btn side-btn-icon"
            :class="{ 'is-active': showOverrideMenu }"
            @click.stop="toggleOverrideMenu"
            aria-label="More actions"
            title="More actions"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <circle cx="5" cy="12" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="19" cy="12" r="2" />
            </svg>
          </button>
          <div v-if="showOverrideMenu" @click.stop class="overrides-menu">
            <div class="overrides-menu-head">Overrides</div>
            <button
              v-if="showChangeBankBtn"
              type="button"
              class="menu-item"
              @click="onOverrideClick('change-bank')"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 3l4 4-4 4M21 7H8M7 21l-4-4 4-4M3 17h13" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Change bank
            </button>
            <button
              v-if="showUpdateUtrBtn"
              type="button"
              class="menu-item menu-item-warn"
              @click="onOverrideClick('update-utr')"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Update UTR
            </button>
            <button
              v-if="showMarkFailedBtn"
              type="button"
              class="menu-item menu-item-danger"
              @click="onOverrideClick('mark-failed')"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M15 9l-6 6M9 9l6 6" stroke-linecap="round" />
              </svg>
              Mark failed
            </button>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
import http from "../../http-common";
import { fetchPayLinkBase } from "../../utils/payLinkBase";
import PayInApproveForm from "./PayInApproveForm.vue";
import AddUtrForm from "./AddUtrForm.vue";
import ClientEditOrderForm from "./ClientEditOrderForm.vue";
import ChangeBankForm from "./ChangeBankForm.vue";
import UpdateUtrForm from "./UpdateUtrForm.vue";
import MarkFailedForm from "./MarkFailedForm.vue";
export default {
  name: "pay-in-item",
  emits: ["refresh"],
  props: ["item", "slNo"],
  components: {
    PayInApproveForm,
    AddUtrForm,
    ClientEditOrderForm,
    ChangeBankForm,
    UpdateUtrForm,
    MarkFailedForm,
  },
  data() {
    return {
      showPayInApproveModal: false,
      showAddUtrModal: false,
      showClientEditModal: false,
      showChangeBankModal: false,
      showUpdateUtrModal: false,
      showMarkFailedModal: false,
      // Vendor overrides menu (Change bank / Update UTR / Mark failed)
      showOverrideMenu: false,
      // Stored so beforeUnmount can detach the document click listener
      // even when the menu was open at unmount time.
      outsideClickHandler: null,
      // The deferred attach is one tick after open so the opening click
      // doesn't immediately close the menu. We track the timer id so
      // rapid open/close/open sequences can cancel the pending attach
      // and avoid leaving a dangling document listener attached.
      outsideClickTimer: null,
      baseToast: {
        type: "",
        message: "",
      },
      error: null,
      selectedItem: null,
      showClientCancelModal: false,
      cancelBusy: false,
      resendBusy: false,
      // Server-configured customer pay-link domain (see utils/payLinkBase).
      payLinkBase: null,
    };
  },
  beforeUnmount() {
    // Clean up any pending deferred-attach timer + the document click
    // listener if the menu was still open when this row unmounted (e.g.
    // list refresh while a menu is open).
    if (this.outsideClickTimer) {
      clearTimeout(this.outsideClickTimer);
      this.outsideClickTimer = null;
    }
    if (this.outsideClickHandler) {
      document.removeEventListener('click', this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
  },
  created() {
    // Resolve the server-configured payment domain once (module-cached —
    // one request per page load, not per row).
    fetchPayLinkBase().then((base) => {
      this.payLinkBase = base;
    });
  },
  computed: {
    getPaymentUrl() {
      // Server-configured payment domain wins; env/origin crafting is only
      // the fallback while the base loads (or when the backend has none).
      if (this.payLinkBase) {
        return `${this.payLinkBase}/#/pay/${this.item.refID}`;
      }
      const base = process.env.VUE_APP_BASE_URL || window.location.origin + '/';
      return `${base}#/pay/${this.item.refID}`;
    },
    getCreatedBy() {
      if (this.item.created_by == 'common_link') {
        return "Common Link";
      } else {
        return this.item.created_by;
      }
    },
    getAmount() {
      if (this.item.txnFee) {
        return this.item.amount;
      } else {
        return parseInt(this.item.amount);
      }
    },
    // Status tone drives both the pill color and the card's left accent bar.
    statusTone() {
      switch (this.item.paymentStatus) {
        case "pending":    return "pending";
        case "approved":   return "success";
        case "rejected":   return "warn";
        case "unassigned": return "neutral";
        case "expired":    return "danger";
        case "failed":     return "danger";
        default:           return "neutral";
      }
    },
    // Kept for template backward-compat callers that use the old class name.
    getClass() {
      if (this.item.paymentStatus == "pending")    return "bg-blue-500";
      if (this.item.paymentStatus == "approved")   return "bg-green-600";
      if (this.item.paymentStatus == "rejected")   return "bg-yellow-600";
      if (this.item.paymentStatus == "unassigned") return "bg-gray-600";
      if (this.item.paymentStatus == "expired")    return "bg-red-600";
      return "bg-black";
    },
    isClient() {
      return !!this.$store.getters.isClient;
    },
    // Client Partner (11102026) — view-only; mirrors the CLIENT's display
    // surface (same visible fields, no action buttons, which are gated to
    // vendor/subadmin/client specifics that a partner never satisfies).
    isClientPartner() {
      return !!this.$store.getters.isClientPartner;
    },
    isAdmin() {
      return !!this.$store.getters.isAdmin;
    },
    isSubAdmin() {
      return !!this.$store.getters.isSubAdmin;
    },
    // Vendor / admin / superadmin oversee multiple branches, so the branch
    // a particular order's bank rolls up to is non-obvious for them.
    // Subadmins (the branch owners themselves) and bank users don't need
    // this row — they already know which branch they're under.
    isVendor() {
      const role = this.$store.getters.role;
      return ['vendor', 'admin', 'superadmin'].includes(role);
    },
    // Per-branch grant from the vendor — only meaningful when the user is
    // a subadmin. Drives whether the Approve button is rendered. Backend
    // re-checks the DB on every approval click so a stale `true` here
    // can't actually approve an order if revoked since login.
    canApproveManually() {
      return (
        this.$store.getters.isSubAdmin &&
        !!this.$store.getters.canApprovePayinManually
      );
    },
    // Aggregate rule for the primary Approve button in the sidebar.
    // Kept as a computed so the template stays readable + so a future
    // change to eligibility touches one place.
    showApproveBtn() {
      if (!this.item.amount) return false;
      // Permission gate (supporting users): main users pass (can() = true).
      if (!(this.can('payin.approve') || this.can('payin.approve_late_expired'))) return false;
      if (this.item.paymentStatus === 'pending' && (this.isVendor || this.canApproveManually)) return true;
      if (this.item.paymentStatus === 'expired' && (this.isAdmin || this.isVendor || this.canApproveManually)) return true;
      return false;
    },
    // Edit-order permissions:
    //   - Client: only their own pending orders.
    //   - Vendor / admin / superadmin: pending OR expired (so they can fix
    //     amount / UTR on a late-paying expired order before re-approving).
    canEditOrder() {
      if (!this.can('payin.edit_utr')) return false;
      if (this.isClient) return this.item.paymentStatus === 'pending';
      if (this.isVendor) return ['pending', 'expired'].includes(this.item.paymentStatus);
      return false;
    },
    // Client-initiated cancel (09082026): clients can kill their own
    // not-yet-approved pay-ins. Backend clears the UTR, flips the order to
    // failed, and fires the failed-order callback; approved orders are
    // rejected server-side, so the button hides for them here.
    showClientCancelBtn() {
      if (!this.can('payin.cancel')) return false;
      return this.isClient && ['pending', 'unassigned', 'expired'].includes(this.item.paymentStatus);
    },
    // Manual bank-change override. Gated to admin / superadmin per spec
    // and intentionally NOT broadcast — surfaced as a low-key text link
    // (see template). For approved orders the backend reverses the old
    // bank's credit and applies the new bank's atomically.
    showChangeBankBtn() {
      if (!this.isVendor || !this.can('payin.change_bank')) return false;
      return ['pending', 'approved', 'expired'].includes(this.item.paymentStatus);
    },
    // Re-fire the success callback (28102026). Vendor-level only; only for an
    // APPROVED (successful) pay-in. Backend replays the stored success payload
    // and logs a new callback_logs entry.
    showResendCallbackBtn() {
      return this.isVendor && this.item.paymentStatus === 'approved';
    },
    // UTR correction on an approved pay-in. Backend chains to
    // callbackHook so the merchant receives a fresh callback with the
    // new UTR.
    showUpdateUtrBtn() {
      if (!this.isVendor || !this.can('payin.edit_utr')) return false;
      return this.item.paymentStatus === 'approved';
    },
    // Per-order mark-failed (mirrors the /callback bulk action).
    // Available on pending / expired / approved — 'approved' reverses
    // bank credit, the other two are pure status flips.
    showMarkFailedBtn() {
      if (!this.isVendor || !this.can('payin.reject')) return false;
      return ['pending', 'expired', 'approved'].includes(this.item.paymentStatus);
    },
    // The ⋯ trigger shows only when at least one override is available
    // for this order — otherwise the menu would open with nothing in it.
    showOverridesTrigger() {
      return this.showChangeBankBtn || this.showUpdateUtrBtn || this.showMarkFailedBtn;
    },
    getApprovalMethod() {
      if (!this.item.approval_method || this.item.paymentStatus !== 'approved') return '-';
      switch (this.item.approval_method) {
        case 'bank_statement': return 'Bank Statement';
        case 'bank_statement_cross_validator': return 'Bank Statement (Reassigned)';
        case 'auto_extension': return 'Extension';
        case 'auto_extension_cross_validator': return 'Extension (Reassigned)';
        case 'manual': return 'Manual';
        default: return this.item.approval_method;
      }
    },
    // Tone drives the pill color of the "Method" chip. Kept in sync with
    // the old approvalMethodColor computed which returned tailwind classes.
    approvalMethodTone() {
      if (!this.item.approval_method || this.item.paymentStatus !== 'approved') return 'neutral';
      switch (this.item.approval_method) {
        case 'bank_statement': return 'info';
        case 'bank_statement_cross_validator': return 'info-2';
        case 'auto_extension': return 'success';
        case 'auto_extension_cross_validator': return 'success-2';
        case 'manual': return 'neutral';
        default: return 'neutral';
      }
    },
    approvalMethodColor() {
      if (!this.item.approval_method || this.item.paymentStatus !== 'approved') return 'text-gray-700';
      switch (this.item.approval_method) {
        case 'bank_statement': return 'text-blue-600';
        case 'bank_statement_cross_validator': return 'text-indigo-600';
        case 'auto_extension': return 'text-emerald-600';
        case 'auto_extension_cross_validator': return 'text-teal-600';
        case 'manual': return 'text-gray-700';
        default: return 'text-gray-700';
      }
    },
    // True when this order was reapproved via a bank statement match
    // after expiring (within the 7-day auto-reapproval window).
    isLateApproval() {
      return this.item.paymentStatus === 'approved'
        && this.item.previous_status === 'expired';
    },
    // True when a cross-validator flow (bank statement upload OR chrome
    // extension push from a different branch) reassigned this order to
    // a new bank before approving. Both flows stamp
    // `original_validator_username` on the row — the additional
    // approval_method check keeps the badge specific to the two known
    // reassignment paths (so a future approval_method that also sets
    // original_validator_username can opt in explicitly).
    isReassignedApproval() {
      return (
        this.item.approval_method === 'bank_statement_cross_validator' ||
        this.item.approval_method === 'auto_extension_cross_validator'
      ) && !!this.item.original_validator_username;
    },
    isUser() {
      return !!this.$store.getters.isUser;
    },
    getApprovedBy() {
      if (this.item.approvedBy == "user") {
        return `${this.item.validatorUsername}(Bank)`;
      } else if (this.item.approvedBy == "subadmin") {
        return `${
          this.item.approvedByUsername
            ? this.item.approvedByUsername
            : this.item.validatorUsername
        }(Branch)`;
      } else if (this.item.approvedBy == "admin") {
        return "Admin";
      } else {
        return "";
      }
    },
  },
  methods: {
    // Permission gate for action buttons. Main users are NOT permission-
    // governed, so hasPermission returns true for them (buttons unchanged);
    // supporting users only see actions they actually hold.
    can(perm) {
      return this.$store.getters.hasPermission(perm);
    },
    // Shallow snapshot of the row's `item` at modal-open time. Modals
    // bind to `selectedItem` (NOT the live `item` prop) so that a socket
    // event in the background — which mutates the order row in the Vuex
    // store via `payin/mergeOrderRow` — can't leak new field values into
    // the open modal mid-edit. Order rows are flat (primitives + small
    // string-array fields the modals don't mutate), so shallow spread
    // is sufficient. Re-cloned on every open so closing + reopening
    // picks up the latest committed values.
    snapshotItem() {
      return { ...this.item };
    },
    addUtr() {
      this.selectedItem = this.snapshotItem();
      this.showAddUtrModal = true;
    },
    formatMoney(v) {
      const n = Number(v) || 0;
      return n.toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    formatDate(inputDate) {
      if (!inputDate) return "";
      const x = String(inputDate).replace("T", " ");
      const y = x.replace(".000Z", "");

      const date = new Date(y);

      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const seconds = date.getSeconds();
      const ampm = hours >= 12 ? "pm" : "am";

      const formattedDate = `${day.toString().padStart(2, '0')}-${month.toString().padStart(2, '0')}-${year} ${hours %
        12}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')} ${ampm}`;

      return formattedDate;
    },
    approveOrder() {
      this.selectedItem = this.snapshotItem();
      this.showPayInApproveModal = true;
    },
    handleError() {
      this.error = null;
    },
    handleBaseToast() {
      this.baseToast = {
        type: "",
        message: "",
      };
    },
    formResponse(status, message) {
      this.showPayInApproveModal = false;
      if (status == "success") {
        this.baseToast.type = status;
        this.baseToast.message = message;
        let $this = this;
        setTimeout(() => {
          $this.handleBaseToast();
        }, 1000);
      } else {
        this.error = message;
      }
    },
    utrFormResponse(status, message) {
      this.showAddUtrModal = false;
      if (status) {
        this.baseToast.type = 'success';
        this.baseToast.message = message;
        let $this = this;
        setTimeout(() => {
          $this.handleBaseToast();
        }, 1000);
      } else {
        this.error = message;
      }
    },
    editOrder() {
      this.selectedItem = this.snapshotItem();
      this.showClientEditModal = true;
    },
    // Overrides menu (⋯). The menu uses a global document click listener
    // for outside-click dismissal: when open, any click that isn't on
    // the menu itself (the menu stops propagation via @click.stop on the
    // trigger + the menu div) bubbles to document and closes the menu.
    toggleOverrideMenu() {
      if (this.showOverrideMenu) {
        this.closeOverrideMenu();
      } else {
        this.openOverrideMenu();
      }
    },
    openOverrideMenu() {
      this.showOverrideMenu = true;
      this.outsideClickHandler = () => this.closeOverrideMenu();
      // Defer attach by one tick — the click that just opened the menu
      // is still propagating to document and would otherwise close the
      // menu immediately. Tracking the timer id so closeOverrideMenu
      // can cancel a still-pending attach (rapid open/close/open would
      // otherwise leak a dangling listener: the second open overwrites
      // outsideClickHandler, the first setTimeout still fires + attaches
      // the new handler, then later removeEventListener only removes one
      // of the two registrations).
      this.outsideClickTimer = setTimeout(() => {
        this.outsideClickTimer = null;
        if (this.showOverrideMenu && this.outsideClickHandler) {
          document.addEventListener('click', this.outsideClickHandler);
        }
      }, 0);
    },
    closeOverrideMenu() {
      this.showOverrideMenu = false;
      if (this.outsideClickTimer) {
        clearTimeout(this.outsideClickTimer);
        this.outsideClickTimer = null;
      }
      if (this.outsideClickHandler) {
        document.removeEventListener('click', this.outsideClickHandler);
        this.outsideClickHandler = null;
      }
    },
    onOverrideClick(action) {
      this.closeOverrideMenu();
      if (action === 'change-bank') this.openChangeBank();
      else if (action === 'update-utr') this.openUpdateUtr();
      else if (action === 'mark-failed') this.openMarkFailed();
    },
    openChangeBank() {
      this.selectedItem = this.snapshotItem();
      this.showChangeBankModal = true;
    },
    handleBankChanged(message) {
      this.showChangeBankModal = false;
      this.baseToast.type = 'success';
      this.baseToast.message = message;
      setTimeout(() => {
        this.handleBaseToast();
        // Parent list refresh — the order's validator info has changed.
        this.$emit('refresh');
      }, 800);
    },
    openUpdateUtr() {
      this.selectedItem = this.snapshotItem();
      this.showUpdateUtrModal = true;
    },
    handleUtrUpdated(message) {
      this.showUpdateUtrModal = false;
      this.baseToast.type = 'success';
      this.baseToast.message = message;
      setTimeout(() => {
        this.handleBaseToast();
        this.$emit('refresh');
      }, 800);
    },
    openMarkFailed() {
      this.selectedItem = this.snapshotItem();
      this.showMarkFailedModal = true;
    },
    handleMarkedFailed(message) {
      this.showMarkFailedModal = false;
      this.baseToast.type = 'success';
      this.baseToast.message = message;
      setTimeout(() => {
        this.handleBaseToast();
        this.$emit('refresh');
      }, 800);
    },
    async confirmClientCancel() {
      this.cancelBusy = true;
      try {
        const res = await http.post(`/orders/${this.item.refID}/client-cancel`);
        this.showClientCancelModal = false;
        this.baseToast.type = 'success';
        this.baseToast.message = res.data?.message || 'Order cancelled successfully.';
        setTimeout(() => {
          this.handleBaseToast();
          this.$emit('refresh');
        }, 800);
      } catch (error) {
        this.showClientCancelModal = false;
        this.baseToast.type = 'error';
        this.baseToast.message = error.response?.data?.message || 'Could not cancel the order.';
        setTimeout(() => this.handleBaseToast(), 1500);
      } finally {
        this.cancelBusy = false;
      }
    },
    async resendCallback() {
      if (this.resendBusy) return;
      this.resendBusy = true;
      try {
        const res = await http.post(`/orders/${this.item.refID}/resend-callback`);
        this.baseToast.type = 'success';
        this.baseToast.message = res.data?.message || 'Success callback re-fired.';
        setTimeout(() => this.handleBaseToast(), 1200);
      } catch (error) {
        this.baseToast.type = 'error';
        this.baseToast.message = error.response?.data?.message || 'Could not resend the callback.';
        setTimeout(() => this.handleBaseToast(), 1800);
      } finally {
        this.resendBusy = false;
      }
    },
    handleClientUpdate(message) {
      this.showClientEditModal = false;
      this.baseToast.type = 'success';
      this.baseToast.message = message;
      setTimeout(() => {
        this.handleBaseToast();
        // Refresh the page to show updated data
        this.$emit('refresh');
      }, 1000);
    },
  },
};
</script>

<style scoped>
/* ==================== Card ==================== */
.pi-card {
  display: grid;
  grid-template-columns: 1fr 200px;
  gap: 0;
  background: #fff;
  border: 1px solid #ede9df;
  border-radius: 12px;
  /* Intentionally NOT overflow:hidden so the ⋯ overrides popover can
     escape the card. Rounded corners are preserved by rounding the
     accent bar's left edges + the sidebar's right edges to match. */
  transition: box-shadow 0.15s, transform 0.15s, border-color 0.15s;
  position: relative;
}
.pi-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #cbd5e1;
  border-radius: 12px 0 0 12px;
  transition: background 0.15s;
}
.pi-card.is-pending::before    { background: linear-gradient(180deg, #f59e0b, #d97706); }
.pi-card.is-approved::before   { background: linear-gradient(180deg, #10b981, #059669); }
.pi-card.is-rejected::before   { background: linear-gradient(180deg, #f59e0b, #b45309); }
.pi-card.is-expired::before    { background: linear-gradient(180deg, #ef4444, #dc2626); }
.pi-card.is-failed::before     { background: linear-gradient(180deg, #ef4444, #dc2626); }
.pi-card.is-unassigned::before { background: linear-gradient(180deg, #94a3b8, #64748b); }

.pi-card:hover {
  border-color: #d9d3c1;
  box-shadow: 0 6px 16px -10px rgba(27, 42, 71, 0.14);
}

/* ==================== Info side ==================== */
.pi-info {
  padding: 12px 14px 12px 16px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pi-identity {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding-bottom: 6px;
  border-bottom: 1px solid #f4f0e5;
}
.pi-receipt {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
}
.receipt-label {
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #94a3b8;
  font-weight: 600;
}
.receipt-value {
  font-family: "SFMono-Regular", Menlo, monospace;
  font-size: 12.5px;
  font-weight: 700;
  color: #1b2a47;
  letter-spacing: 0.02em;
}

.pi-badges {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}
.pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
}
.pill-info { background: #eef2f7; color: #1B2A47; border: 1px solid #cbd5e1; }
.pill-warn { background: #fef3c7; color: #b45309; border: 1px solid #fde68a; }

/* ---------- Info grid — inline label:value pairs for density ---------- */
.pi-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 20px;
  row-gap: 4px;
}
.pi-col {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.pi-field {
  display: grid;
  grid-template-columns: 96px 1fr;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  padding: 1px 0;
}
.field-label {
  font-size: 10.5px;
  letter-spacing: 0.04em;
  color: #94a3b8;
  font-weight: 500;
  text-transform: none;
  line-height: 1.4;
}
.field-value {
  font-size: 12.5px;
  color: #1b2a47;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex-wrap: wrap;
  line-height: 1.4;
}
.field-value > span,
.field-value > a {
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
/*
  `.field-value .mono` used to swap the value to a monospace font at 11.5px
  for machine identifiers (Order ID / UPI / UTR). Dropped so every value in
  a card reads with the same face + size as name-style fields — visual
  consistency won over identifier legibility. Dead `.mono` class refs on
  <span>s are silently no-op. The `.break` rule below is unrelated and
  still needed for long IDs that would otherwise overflow.
*/
.field-value .break { word-break: break-all; overflow-wrap: anywhere; }
.link {
  color: #1B2A47;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.15s;
}
.link:hover { color: #0f172a; text-decoration: underline; }

/* Approval method chip */
.method-chip {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 5px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.method-neutral  { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
.method-info     { background: #dbeafe; color: #1e40af; border: 1px solid #bfdbfe; }
.method-info-2   { background: #e0e7ff; color: #4338ca; border: 1px solid #c7d2fe; }
.method-success  { background: #d1fae5; color: #047857; border: 1px solid #a7f3d0; }
.method-success-2{ background: #ccfbf1; color: #0f766e; border: 1px solid #99f6e4; }

/* ==================== Side ==================== */
.pi-side {
  background: #fbfaf7;
  border-left: 1px solid #f1ede2;
  border-radius: 0 12px 12px 0;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  position: relative;
}

.status-pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #fff;
  border: 1px solid transparent;
}
.status-pending    { background: linear-gradient(135deg, #f59e0b, #d97706); }
.status-success    { background: linear-gradient(135deg, #10b981, #059669); }
.status-warn       { background: linear-gradient(135deg, #f59e0b, #b45309); }
.status-danger     { background: linear-gradient(135deg, #ef4444, #dc2626); }
.status-neutral    { background: linear-gradient(135deg, #94a3b8, #64748b); }

.amount-value {
  font-size: 19px;
  font-weight: 800;
  color: #1b2a47;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  margin-top: 2px;
  line-height: 1.1;
}

.side-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
  width: 100%;
  align-items: stretch;
}
.side-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
}
.side-btn svg { width: 13px; height: 13px; }

.side-btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 14px -6px rgba(27, 42, 71, 0.45);
}
.side-btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px -8px rgba(27, 42, 71, 0.55);
}
.side-btn-secondary {
  background: #fff;
  color: #1B2A47;
  border-color: #cbd5e1;
}
.side-btn-secondary:hover { background: #eef2f7; border-color: #94a3b8; }
.side-btn-ghost {
  background: #fef2f2;
  color: #b91c1c;
  border-color: #fecaca;
}
.side-btn-ghost:hover { background: #fee2e2; color: #991b1b; }

.side-btn-icon {
  padding: 0;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: transparent;
  color: #94a3b8;
  border-color: transparent;
  align-self: center;
  margin-top: 2px;
}
.side-btn-icon svg { width: 15px; height: 15px; }
.side-btn-icon:hover { background: #f1ede2; color: #1b2a47; }
.side-btn-icon.is-active { background: #eef2f7; color: #1B2A47; }

/* ---------- Overrides menu ---------- */
.overrides-wrap {
  position: relative;
  display: flex;
  justify-content: center;
}
.overrides-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  background: #fff;
  border: 1px solid #ede9df;
  border-radius: 12px;
  box-shadow: 0 12px 32px -12px rgba(15, 23, 42, 0.22);
  width: 180px;
  padding: 6px;
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.overrides-menu-head {
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #94a3b8;
  font-weight: 700;
  padding: 6px 10px 4px;
}
.menu-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 500;
  color: #334155;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.12s;
  font-family: inherit;
  text-align: left;
}
.menu-item svg { width: 14px; height: 14px; }
.menu-item:hover { background: #fbfaf7; color: #1b2a47; }
.menu-item-warn { color: #b45309; }
.menu-item-warn:hover { background: #fef3c7; color: #92400e; }
.menu-item-danger { color: #b91c1c; }
.menu-item-danger:hover { background: #fef2f2; color: #991b1b; }

/* ==================== Responsive ==================== */
@media (max-width: 900px) {
  .pi-card {
    grid-template-columns: 1fr;
  }
  .pi-side {
    border-left: none;
    border-top: 1px solid #f1ede2;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
  }
  .amount-value { margin-top: 0; }
  .side-actions {
    flex-direction: row;
    flex-wrap: wrap;
    width: auto;
    justify-content: flex-end;
    margin-top: 0;
  }
  .side-btn-icon { align-self: auto; margin-top: 0; }
}
@media (max-width: 640px) {
  .pi-grid { grid-template-columns: 1fr; }
  .pi-info { padding: 10px 12px 10px 14px; }
  .pi-field { grid-template-columns: 80px 1fr; }
}

/* ==================== Modals (teleported to body) ==================== */
.pi-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.pi-modal-card {
  background: #fff;
  border-radius: 18px;
  width: 100%;
  max-width: 520px;
  max-height: calc(100vh - 40px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px -20px rgba(15, 23, 42, 0.35);
  border: 1px solid #ede9df;
}
.pi-modal-wide { max-width: 720px; }

.pi-modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 22px 14px;
  border-bottom: 1px solid #f1ede2;
}
.pi-modal-head-warn { background: linear-gradient(180deg, #fefce8 0%, #fff 100%); border-bottom-color: #fde68a; }
.pi-modal-head-danger { background: linear-gradient(180deg, #fef2f2 0%, #fff 100%); border-bottom-color: #fecaca; }

.pi-modal-eyebrow {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 700;
}
.pi-modal-eyebrow.warn { color: #b45309; }
.pi-modal-eyebrow.danger { color: #b91c1c; }

.pi-modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #1b2a47;
  margin: 2px 0 4px;
  letter-spacing: -0.01em;
}
.pi-modal-sub { font-size: 12.5px; color: #64748b; line-height: 1.4; }

.pi-modal-close {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 30px; height: 30px;
  border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all 0.12s;
  flex-shrink: 0;
}
.pi-modal-close:hover { background: #f1f5f9; color: #1b2a47; }
.pi-modal-close svg { width: 16px; height: 16px; }

.pi-modal-body {
  padding: 18px 22px 22px;
  overflow-y: auto;
  max-height: calc(100vh - 200px);
}

.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.22s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter-active .pi-modal-card, .modal-fade-leave-active .pi-modal-card {
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}
.modal-fade-enter-from .pi-modal-card { transform: translateY(-8px) scale(0.98); }
</style>
